// Client-side image processing using HTML5 Canvas.
// Supports: stretch, auto-crop (cover), fit (contain with bg color or blur).

export function loadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve({ img, dataUrl: reader.result });
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function processImage({ img, targetW, targetH, method = "auto-crop", bg = "white", quality = 90, format = "jpeg" }) {
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  if (method === "stretch") {
    ctx.drawImage(img, 0, 0, targetW, targetH);
  } else if (method === "auto-crop") {
    // cover - scale to fully cover, center crop
    const scale = Math.max(targetW / img.width, targetH / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    const x = (targetW - w) / 2;
    const y = (targetH - h) / 2;
    ctx.drawImage(img, x, y, w, h);
  } else if (method === "fit") {
    // contain with bg
    if (bg === "blur") {
      // draw a heavily-blurred cover version as bg first
      const coverScale = Math.max(targetW / img.width, targetH / img.height);
      const cw = img.width * coverScale;
      const ch = img.height * coverScale;
      const cx = (targetW - cw) / 2;
      const cy = (targetH - ch) / 2;
      ctx.filter = "blur(40px) brightness(0.85)";
      ctx.drawImage(img, cx, cy, cw, ch);
      ctx.filter = "none";
    } else {
      ctx.fillStyle = bg === "black" ? "#000000" : "#FFFFFF";
      ctx.fillRect(0, 0, targetW, targetH);
    }
    const scale = Math.min(targetW / img.width, targetH / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    const x = (targetW - w) / 2;
    const y = (targetH - h) / 2;
    ctx.drawImage(img, x, y, w, h);
  } else {
    ctx.drawImage(img, 0, 0, targetW, targetH);
  }

  const mime = format === "png" ? "image/png" : format === "webp" ? "image/webp" : "image/jpeg";
  const q = format === "png" ? undefined : Math.max(0.1, Math.min(1, quality / 100));
  const dataUrl = canvas.toDataURL(mime, q);
  return { dataUrl, canvas, mime };
}

export function makeThumbnail(dataUrl, size = 160) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(size / img.width, size / img.height);
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = () => resolve("");
    img.src = dataUrl;
  });
}

export function dataUrlBytes(dataUrl) {
  // approximate decoded size
  const base64 = dataUrl.split(",")[1] || "";
  return Math.ceil(base64.length * 3 / 4);
}

export function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export async function downloadAllAsZip(items) {
  // Lazy load JSZip via CDN as fallback if not bundled
  if (items.length === 1) {
    downloadDataUrl(items[0].dataUrl, items[0].filename);
    return;
  }
  // sequential downloads if no zip support
  for (const it of items) {
    downloadDataUrl(it.dataUrl, it.filename);
    await new Promise((r) => setTimeout(r, 250));
  }
}
